import { useState } from 'react'
import { Modal } from '@/components/lc'
import { Icon } from '@/components/Icon'
import type { CommunityHue } from '@/styles/lifecraft'
import { cn } from '@/lib/utils'
import { useAuth, loginUrl, communityHomeUrl } from '@/lib/auth'
import {
  quizQuestions,
  quizCategories,
  coinRules,
  redemptionStore,
} from '@/data/musicQuiz'

/* ================================================================== *
 *  Music Explorer Challenge — the Rhythm daily quiz, played in a      *
 *  modal. Client-side only: score, streaks and coins are computed     *
 *  live; nothing is persisted. Three phases: intro → playing → done.  *
 * ================================================================== */

type Phase = 'playing' | 'done'

const catMeta = (key: string) => quizCategories.find((c) => c.key === key)

/* Fisher–Yates: a fresh question order for every play, so no two users get the
   same run. */
function shuffle<T>(arr: readonly T[]): T[] {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

/* A small coin token — used everywhere the quiz talks about coins, in place of
   an emoji so it matches the site's line-icon language. */
function Coin({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className="inline-block shrink-0 align-[-0.15em] text-amber-500" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.16" />
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <text x="12" y="16.5" textAnchor="middle" fontSize="12" fontWeight="700" fill="currentColor">₹</text>
    </svg>
  )
}

export function MusicQuizModal({
  open,
  onClose,
  hue,
}: {
  open: boolean
  onClose: () => void
  hue: CommunityHue
}) {
  // Each play gets its own shuffled order.
  const [questions, setQuestions] = useState(() => shuffle(quizQuestions))
  const [phase, setPhase] = useState<Phase>('playing')
  const [idx, setIdx] = useState(0)
  const [picked, setPicked] = useState<number | null>(null)
  const [correct, setCorrect] = useState(0)
  const [coins, setCoins] = useState(0)
  const [streak, setStreak] = useState(0)
  const [bestStreak, setBestStreak] = useState(0)
  const [lastBonus, setLastBonus] = useState(0) // streak bonus just earned (for the flash)

  const q = questions[idx]
  const total = questions.length
  const answered = picked !== null

  function reset() {
    setQuestions(shuffle(quizQuestions)); setPhase('playing'); setIdx(0); setPicked(null); setCorrect(0)
    setCoins(0); setStreak(0); setBestStreak(0); setLastBonus(0)
  }

  function close() { onClose(); setTimeout(reset, 250) }

  function choose(option: number) {
    if (answered) return
    setPicked(option)
    if (option === q.correct) {
      const nextStreak = streak + 1
      let gain = coinRules.perCorrect
      let bonus = 0
      if (nextStreak % coinRules.streakEvery === 0) bonus = coinRules.streakBonus
      setCorrect((c) => c + 1)
      setStreak(nextStreak)
      setBestStreak((b) => Math.max(b, nextStreak))
      setCoins((c) => c + gain + bonus)
      setLastBonus(bonus)
    } else {
      setStreak(0)
      setLastBonus(0)
    }
  }

  function next() {
    if (idx + 1 < total) {
      setIdx((i) => i + 1); setPicked(null); setLastBonus(0)
      return
    }
    // finished the last question → tally session bonuses
    const finalCorrect = correct
    let extra = coinRules.completionBonus
    if (finalCorrect === total) extra += coinRules.perfectBonus
    setCoins((c) => c + extra)
    setPhase('done')
  }

  const { authed } = useAuth()
  // Join Rhythm: logged-in members go straight to the Rhythm community in the
  // webapp; guests go to login (which redirects back here afterwards).
  const joinHref = (authed && communityHomeUrl('Rhythm')) || loginUrl()

  const deep = hue.deep
  const pill = 'rounded-pill px-5 py-2.5 text-[14px] font-bold transition-transform duration-300 ease-brand hover:scale-[1.02]'

  return (
    <Modal open={open} onClose={close} title="Music Explorer Challenge" size="lg" theme="light">
      {/* ---------- PLAYING ---------- */}
      {phase === 'playing' && (
        <div>
          {/* progress + running coins */}
          <div className="flex items-center justify-between gap-4">
            <p className="label !text-[11px] text-lc-mute">
              Question {idx + 1} <span className="opacity-50">/ {total}</span>
            </p>
            <div className="flex items-center gap-3 text-[13px]">
              {streak >= 2 && <span className="inline-flex items-center gap-1 font-semibold" style={{ color: deep }}><Icon name="sparkle" size={13} /> {streak} streak</span>}
              <span className="inline-flex items-center gap-1 font-bold text-lc-ink"><Coin /> {coins}</span>
            </div>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-pill bg-black/10">
            <div className="h-full rounded-pill transition-all duration-500" style={{ width: `${((idx + (answered ? 1 : 0)) / total) * 100}%`, background: deep }} />
          </div>

          <div className="mt-5 flex items-center gap-2">
            <span className="lc-glass-soft inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-[12px] font-semibold text-lc-ink">
              <Icon name={catMeta(q.category)?.icon ?? 'star'} size={13} />{catMeta(q.category)?.label}
            </span>
          </div>
          <h3 className="mt-3 font-display text-xl leading-snug text-lc-ink sm:text-[22px]">{q.q}</h3>

          <div className="mt-4 grid gap-2.5">
            {q.options.map((opt, i) => {
              const isCorrect = i === q.correct
              const isPicked = i === picked
              const show = answered && (isCorrect || isPicked)
              return (
                <button
                  key={i}
                  onClick={() => choose(i)}
                  disabled={answered}
                  className={cn(
                    'flex items-center gap-3 rounded-card border px-4 py-3 text-left text-[15px] transition-colors duration-200',
                    !answered && 'border-black/10 hover:border-black/25 hover:bg-black/[0.03]',
                    answered && !show && 'border-black/5 text-lc-mute opacity-60',
                    show && isCorrect && 'border-emerald-500/60 bg-emerald-500/10 text-emerald-800',
                    show && isPicked && !isCorrect && 'border-rose-500/60 bg-rose-500/10 text-rose-800',
                  )}
                >
                  <span
                    className={cn(
                      'grid h-6 w-6 shrink-0 place-items-center rounded-full text-[12px] font-bold',
                      !answered && 'bg-black/[0.06] text-lc-mute',
                      show && isCorrect && 'bg-emerald-500 text-white',
                      show && isPicked && !isCorrect && 'bg-rose-500 text-white',
                      answered && !show && 'bg-black/[0.04] text-lc-mute',
                    )}
                  >
                    {show && isCorrect ? '✓' : show && isPicked && !isCorrect ? '✕' : String.fromCharCode(65 + i)}
                  </span>
                  <span className="font-medium">{opt}</span>
                </button>
              )
            })}
          </div>

          {answered && (
            <div className="mt-4 flex items-center justify-between gap-4">
              <p className="text-[13px] font-medium" style={{ color: picked === q.correct ? deep : undefined }}>
                {picked === q.correct
                  ? lastBonus
                    ? `Correct! +${coinRules.perCorrect} coins  ·  streak bonus +${lastBonus}`
                    : `Correct! +${coinRules.perCorrect} coins`
                  : <span className="text-rose-700">Not quite. The answer is {q.options[q.correct]}.</span>}
              </p>
              <button onClick={next} className={cn(pill, 'bg-lime text-night')}>
                {idx + 1 < total ? 'Next →' : 'See results →'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---------- DONE ---------- */}
      {phase === 'done' && (
        <div className="text-center">
          {/* the points scored */}
          <p className="label !text-[11px] text-lc-mute">You scored</p>
          <p className="mt-1 text-[15px] text-lc-mute"><b className="text-lc-ink">{correct}/{total}</b> correct</p>

          <div className="mx-auto mt-5 flex max-w-xs items-center justify-center gap-2 rounded-card px-5 py-4" style={{ background: `${deep}12` }}>
            <Coin size={26} />
            <span className="font-display text-3xl text-lc-ink">{coins}</span>
            <span className="label !text-[11px] text-lc-mute">LifeCoins earned</span>
          </div>

          <div className="mx-auto mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[12px] text-lc-mute">
            <span>{correct} correct · +{correct * coinRules.perCorrect}</span>
            {bestStreak >= coinRules.streakEvery && <span>streaks · +{Math.floor(bestStreak / coinRules.streakEvery) * coinRules.streakBonus}</span>}
            <span>completion · +{coinRules.completionBonus}</span>
            {correct === total && <span>perfect · +{coinRules.perfectBonus}</span>}
          </div>

          {/* impact: why joining Rhythm matters */}
          <p className="mx-auto mt-6 max-w-sm text-[15px] leading-relaxed text-lc-ink">
            Join Rhythm to keep playing, singing and jamming, and earn LifeCoins as you go.
          </p>

          {/* where LifeCoins can be redeemed */}
          <div className="mt-5 text-left">
            <p className="label !text-[11px] text-lc-mute">Redeem your LifeCoins for</p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {redemptionStore.slice(0, 3).map((r) => (
                <div key={r.title} className="lc-glass-soft rounded-card px-3 py-2.5">
                  <div style={{ color: deep }}><Icon name={r.icon} size={18} /></div>
                  <p className="mt-0.5 text-[12px] font-semibold leading-tight text-lc-ink">{r.title}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-3">
            <button onClick={reset} className={cn(pill, 'lc-glass-soft text-lc-ink')}>Play again</button>
            <a href={joinHref} onClick={close} className={cn(pill, 'bg-lime text-night')}>Join Rhythm</a>
          </div>
        </div>
      )}
    </Modal>
  )
}
